import { makeStyles } from "@material-ui/core/styles";
import React from "react";
export const FIELD_HEIGHT = 45;
export const C = {
    primary: "#003a6b",
    success: "#16B364",
    warning: "#E07B00",
    error: "#E84040",
    border: "#E2E5EB",
    text: "#1A1D23",
    textMuted: "#636B80",
    textFaint: "#6c6e75",
    srcColor: "#3B6FE8",
    midColor: "#E07B00",
    destColor: "#16B364",
};



const muiInputOverride = {
    "& .MuiOutlinedInput-root": {
        height: FIELD_HEIGHT,
        borderRadius: 10,
        fontSize: 14,
        background: "#fff",
        "& input": { padding: "0 14px", fontSize: 7, color: "#374151" },
        "& fieldset": { borderWidth: "1.5px", borderColor: "#d1d5db" },
        "&:hover fieldset": { borderColor: "#2563eb" },
        "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-outlined": {
        transform: "translate(14px, 12px) scale(1)",
        fontSize: 14,
        color: "#9ca3af",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
    },
    "& .MuiFormHelperText-root": { fontSize: 11 },
};


export const useStyles = makeStyles(() => ({
    root: { width: "100%", marginTop: "7rem" },
    // Applies the CSS variables to MUI TextField / Autocomplete
    geoField: muiInputOverride,
     grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  },
   readOnlyNote: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#F0F4FA",
    border: "1px dashed #C5D0E6",
    borderRadius: 8,
    padding: "6px 10px",
    marginBottom: 10,
  },
   readOnlyField: {
    "& .MuiOutlinedInput-root": {
      background: "#F7F9FD !important",
      "& fieldset": {
        borderColor: "#E3EAF5 !important",
        borderStyle: "dashed !important",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "#5C78B5 !important",
      fontStyle: "italic",
    },
  },
   lbl: {
    fontSize: 11,
    fontWeight: 700,
    color: "#4A5880",
    marginLeft: "4px",
    display: "flex",
    alignItems: "center",
    gap: 5,
    letterSpacing: ".02em",
    "& .req": { color: "#E04545", fontWeight: 800 },
  },

  lblDim: { color: "#94A3B8 !important" },

  inputRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },


}));

export const getCurrentTime = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
};

export const MARKER_COLORS = {
    src: C.srcColor,   // e.g. "#F97316" orange
    mid: C.midColor,   // e.g. "#EAB308" yellow
    dest: C.destColor, // e.g. "#22C55E" green
};

// ── Geo math (ported from Kotlin LatLngOffsetUtils) ──────
const EARTH_RADIUS = 6371000.0;

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

export const getOffsetLatLng = (center, bearing, distanceMeters) => {
    const lat1 = toRad(center.lat);
    const lon1 = toRad(center.lng);
    const bearingRad = toRad(bearing);
    const d = distanceMeters / EARTH_RADIUS;

    const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(d) +
        Math.cos(lat1) * Math.sin(d) * Math.cos(bearingRad)
    );
    const lon2 = lon1 + Math.atan2(
        Math.sin(bearingRad) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );

    return { lat: toDeg(lat2), lng: toDeg(lon2) };
};

// Builds left+right corridor points for each stop
export const buildGeofenceFromRoute = (points, distanceMeters) => {
    const corridorPoints = [];
    points.forEach((pt, i) => {
        // Determine heading from this point to next (or previous if last)
        const next = points[i + 1] || points[i - 1];
        if (!next) return;

        const heading = toDeg(Math.atan2(
            toRad(next.lng - pt.lng),
            toRad(next.lat - pt.lat)
        ));

        const leftBearing  = (heading - 90 + 360) % 360;
        const rightBearing = (heading + 90) % 360;

        corridorPoints.push({
            stopName: pt.name,
            center: { lat: pt.lat, lng: pt.lng },
            left:  getOffsetLatLng({ lat: pt.lat, lng: pt.lng }, leftBearing, distanceMeters),
            right: getOffsetLatLng({ lat: pt.lat, lng: pt.lng }, rightBearing, distanceMeters),
        });
    });
    return corridorPoints;
};

// Add after imports, before MOCK_ROUTES
export const MidpointTooltip = ({ midpoints }) => {
    const [visible, setVisible] = React.useState(false);
    const ref = React.useRef(null);
    const [pos, setPos] = React.useState({ top: 0, left: 0 });

    if (!midpoints || midpoints.length === 0) {
        return <span style={{ fontSize: "12px", color: "#9CA3AF" }}>— No stops</span>;
    }

    const handleMouseEnter = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setPos({
            top: rect.bottom + window.scrollY + 6,
            left: rect.left + window.scrollX,
        });
        setVisible(true);
    };

    return (
        <>
            <span
                ref={ref}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setVisible(false)}
                style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    fontSize: "12px", color: "#D97706", fontWeight: 600,
                    background: "#FFF3E0", border: "1px solid #FDE68A",
                    borderRadius: 20, padding: "2px 10px", cursor: "default",
                    userSelect: "none",
                }}
            >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#D97706" strokeWidth="2.5" />
                    <circle cx="12" cy="12" r="3" fill="#D97706" />
                </svg>
                {midpoints.length} stop{midpoints.length !== 1 ? "s" : ""}
            </span>

            {visible && (
                <div style={{
                    position: "fixed",
                    top: pos.top,
                    left: pos.left,
                    zIndex: 9999,
                    background: "#1a2035",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12,
                    padding: "10px 0",
                    minWidth: 200,
                    maxWidth: 280,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                    pointerEvents: "none",
                }}>
                    {/* Header */}
                    <div style={{
                        padding: "0 14px 8px",
                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                        marginBottom: 6,
                        display: "flex", alignItems: "center", gap: 6,
                    }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="#f97316" strokeWidth="2.5" />
                            <circle cx="12" cy="12" r="3" fill="#f97316" />
                        </svg>
                        <span style={{
                            fontSize: 10, fontWeight: 700, color: "#94a3b8",
                            textTransform: "uppercase", letterSpacing: "0.8px",
                        }}>
                            Midpoints · {midpoints.length} stop{midpoints.length !== 1 ? "s" : ""}
                        </span>
                    </div>

                    {/* Stop list */}
                    {midpoints.map((m, i) => (
                        <div key={i} style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "6px 14px",
                            borderBottom: i < midpoints.length - 1
                                ? "1px solid rgba(255,255,255,0.05)" : "none",
                        }}>
                            {/* Index badge */}
                            <div style={{
                                width: 20, height: 20, borderRadius: "50%",
                                background: "#f97316", color: "#fff",
                                fontSize: 10, fontWeight: 700,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                            }}>
                                {i + 1}
                            </div>

                            {/* Name */}
                            <span style={{
                                fontSize: 12, fontWeight: 600, color: "#e2e8f0",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>
                                {m?.name || m || `Stop ${i + 1}`}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export const getRouteColor = (index) => {
    const colors = [
        "#1e40af", // Dark Blue
        "#15803d", // Dark Green
        "#b45309", // Dark Amber/Orange
        "#7f1d1d", // Dark Red
        "#6d28d9", // Dark Purple
        "#be185d", // Dark Pink
        "#0891b2", // Dark Cyan
        "#0d9488", // Dark Teal
    ];
    return colors[index % colors.length];
};


// ── Icon components (add near top of file, below existing helpers) ──
export const DistanceIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="19" r="2.5" />
        <circle cx="18" cy="5" r="2.5" />
        <path d="M8.2 17.5C11 14 9 10 12 8s7-1 7.5 2" />
    </svg>
);

export const DurationIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" />
    </svg>
);

export const MidpointsIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="6" cy="6" r="2.5" />
        <circle cx="18" cy="18" r="2.5" />
        <circle cx="12" cy="12" r="2.5" />
        <path d="M8 7.5L10.3 10M13.7 14L16 16.5" />
    </svg>
);


// ── Add alongside the other icon components ──
export const DwellIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4l2.5 1.5" />
    </svg>
);

export const DeviationIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 18c4-8 6-10 8-10s3 3 3 6 1 6 3 6" />
        <circle cx="4" cy="18" r="1.4" fill={color} stroke="none" />
        <circle cx="18" cy="20" r="1.4" fill={color} stroke="none" />
    </svg>
);

export const UnauthStopIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M8 8l8 8" />
    </svg>
);

export const CompletionIcon = ({ color }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3.5" y="5" width="17" height="16" rx="2.5" />
        <path d="M3.5 10h17" />
        <path d="M8 3v4M16 3v4" />
        <path d="M8.5 14.5l2 2 4-4.5" />
    </svg>
);